import classNames from 'classnames';
import { useCombobox } from 'downshift';
import React, { forwardRef, useCallback, useEffect, useState, memo } from 'react';
import { FormFields, GeoSuggestion } from '../../common/types';
import useDebounce from '../../common/useDebounce';
import { getGeoCode as defaultGetGeoCode } from '../../common/utils';
import Input from '../Input/Input';
import styles from './GeoSuggest.module.scss';

export interface GeoSuggestProps {
    label?: string | null;
    placeholder?: string | null;
    value?: string;
    onValidAddressChange?: ((value: GeoSuggestion) => void) | null;
    onChange?: ((value: string) => void) | null;
    onClear?: VoidFunction | null;
    error?: string | null;
    className?: string | null;
    name?: keyof FormFields | null;
    clearErrors?: (name: keyof FormFields) => void;
    lang?: string;
}

const defaultProps: GeoSuggestProps = {
    label: null,
    placeholder: '',
    value: '',
    onChange: null,
    onValidAddressChange: null,
    onClear: null,
    error: null,
    className: '',
    name: null,
    clearErrors() {},
    lang: 'ru',
};

export const generateGeoSuggest = (
    getGeoCode: (address: string, lang: string) => Promise<GeoSuggestion[]>
) => {
    const GeoSuggest = forwardRef<HTMLInputElement, GeoSuggestProps>(
        (
            {
                label,
                placeholder,
                error,
                onChange,
                onValidAddressChange,
                value,
                name,
                clearErrors,
                lang,
            }: GeoSuggestProps,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ref
        ) => {
            const [items, setItems] = useState<GeoSuggestion[]>([]);
            const clearError = useCallback(() => {
                if (clearErrors && name) clearErrors(name);
            }, [clearErrors, name]);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const onInputValueChange = ({ target }) => {
                if (onChange) onChange(target.value);
            };
            const {
                isOpen,
                getMenuProps,
                getInputProps,
                highlightedIndex,
                getItemProps,
                // selectedItem,
                selectItem,
            } = useCombobox({
                inputValue: value,
                items,
                itemToString: (item) => item?.name ?? '',
                // eslint-disable-next-line @typescript-eslint/no-shadow
                onSelectedItemChange: ({ selectedItem }) => {
                    clearError();
                    if (onValidAddressChange) onValidAddressChange(selectedItem as GeoSuggestion);
                },
            });

            const debouncedAddress = useDebounce(value ?? '', 600);
            useEffect(() => {
                if (debouncedAddress) {
                    getGeoCode(debouncedAddress, lang ?? 'ru')
                        .then((data) => setItems(data))
                        .catch(() => {});
                }
            }, [debouncedAddress, lang]);

            return (
                <div className={classNames(styles.container)} data-testid="geo-suggest-container">
                    <Input
                        {...getInputProps()}
                        data-testid="geo-suggest-input"
                        onClear={() => selectItem(null)}
                        label={label}
                        placeholder={placeholder}
                        className={styles.input}
                        error={error}
                        value={value}
                        onChange={onInputValueChange}
                        // ref={ref}
                    />
                    <ul
                        className={classNames(styles.dropdown, {
                            [styles.visible]: isOpen && items.length > 0,
                        })}
                        data-testid="items-ul"
                        {...getMenuProps()}
                    >
                        {items.map((item, index) => (
                            <li
                                className={classNames(styles.item, {
                                    [styles.highlight]: highlightedIndex === index,
                                })}
                                key={`${item.name}`}
                                {...getItemProps({
                                    item,
                                    index,
                                })}
                            >
                                {item.name}
                            </li>
                        ))}
                    </ul>
                </div>
            );
        }
    );
    GeoSuggest.defaultProps = defaultProps;
    return GeoSuggest;
};

const GeoSuggest = generateGeoSuggest(defaultGetGeoCode);

export default memo(GeoSuggest);
